/**
 * 設定頁 Shell（REQ-20260419003）。
 *
 * 提供：
 * - 左側 nav（依使用者權限過濾；資料字典 / 管理兩組間加分隔線）
 * - 右側 `<Outlet />` 給子路由 render
 * - `/settings` 本身會 redirect 到該使用者第一個可存取模組（由 defaultSettingsModule 決定）
 * - 進入某模組路徑若無權限，redirect 到預設模組
 */
import { NavLink, Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '../features/auth/store';
import {
  SETTINGS_MODULES,
  accessibleModules,
  defaultSettingsModule,
} from '../features/settings/modules';

export function SettingsLayout(): JSX.Element {
  const me = useAuthStore((s) => s.user);
  const location = useLocation();
  const modules = accessibleModules(me?.role);
  const defaultPath = defaultSettingsModule(me?.role);

  // `/settings`（根）→ redirect 到預設模組
  if (location.pathname === '/settings' || location.pathname === '/settings/') {
    if (defaultPath) {
      return <Navigate to={`/settings/${defaultPath}`} replace />;
    }
    return (
      <div role="alert" className="text-slate-500">
        沒有可存取的設定模組
      </div>
    );
  }

  // 進入某 `/settings/<sub>` 時，若該子路徑對應的模組不存取範圍內，導回預設
  const currentSub = location.pathname.replace(/^\/settings\//, '');
  const targetModule = SETTINGS_MODULES.find((m) =>
    matchSubPath(currentSub, m.path),
  );
  const canAccessCurrent =
    targetModule && me && targetModule.allowedRoles.includes(me.role);
  if (targetModule && !canAccessCurrent && defaultPath) {
    return <Navigate to={`/settings/${defaultPath}`} replace />;
  }

  const dataModules = modules.filter((m) => m.navGroup === 'data' && m.navLabel);
  const adminModules = modules.filter(
    (m) => m.navGroup === 'admin' && m.navLabel,
  );

  return (
    <section className="grid grid-cols-[12rem_1fr] gap-6">
      <aside className="border-r pr-4" data-testid="settings-nav">
        <h1 className="text-lg font-semibold mb-3">系統設定</h1>
        <nav className="grid gap-1" role="navigation">
          {dataModules.map((m) => (
            <SettingsNavLink key={m.id} to={`/settings/${m.path}`}>
              {m.navLabel}
            </SettingsNavLink>
          ))}
          {dataModules.length > 0 && adminModules.length > 0 ? (
            <hr className="my-2 border-slate-200" />
          ) : null}
          {adminModules.map((m) => (
            <SettingsNavLink key={m.id} to={`/settings/${m.path}`}>
              {m.navLabel}
            </SettingsNavLink>
          ))}
        </nav>
      </aside>
      <div>
        <Outlet />
      </div>
    </section>
  );
}

/**
 * 判斷實際的 sub path（例：`users/5/edit`）是否 match 某模組的 path
 * pattern（例：`users/:id/edit`）。只做樸素的分段比對。
 */
function matchSubPath(actual: string, pattern: string): boolean {
  const a = actual.split('/').filter(Boolean);
  const p = pattern.split('/').filter(Boolean);
  if (a.length !== p.length) return false;
  return p.every((seg, i) => seg.startsWith(':') || seg === a[i]);
}

function SettingsNavLink(props: {
  to: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <NavLink
      to={props.to}
      end
      className={({ isActive }) =>
        isActive
          ? 'rounded px-2 py-1.5 bg-blue-600 text-white text-sm'
          : 'rounded px-2 py-1.5 text-sm text-slate-700 hover:bg-slate-100'
      }
    >
      {props.children}
    </NavLink>
  );
}
